import { Box, Typography } from '@mui/material';
import { setProviderRegular, patchProviderDetails } from '@uplift/api';
import { ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser, useCurrentuserRefetch } from '@uplift/contexts';
import { Button, Loader, SubmitButton } from '@uplift/ui-v2';
import { Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { StickyButton } from '../../components';
import { PayorEnrollmentFormContent } from './PayorEnrollmentFormContent';
import { PayorEnrollmentForm, payorEnrollmentInitialValues, payorEnrollmentValidation } from './validation';

export const PayorEnrollment: React.VFC = () => {
  const { push } = useHistory();
  const me = useCurrentuser();
  const { refetchUser } = useCurrentuserRefetch();
  const [ submitError, setSubmitError ] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const finishOnboarding = useCallback(async () => {
    await setProviderRegular({ id: me.id });
    refetchUser();
    push(ROUTING_CONFIG.main);
  }, [ me?.id, push, refetchUser ]);

  const handleSubmit = useCallback(async (values: PayorEnrollmentForm) => {
    setSubmitError(null);
    try {
      await patchProviderDetails(me.id, {
        ...(values.npi ? { npi: values.npi } : {}),
        caqhId: values.caqhId,
        caqhUsername: values.caqhUsername,
        caqhPassword: values.caqhPassword,
      });
      await finishOnboarding();
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    }
  }, [ me?.id, finishOnboarding ]);

  const handleSkip = useCallback(async () => {
    await finishOnboarding();
  }, [ finishOnboarding ]);

  if (!me) {
    return <Loader />;
  }

  return (
    <Box width="100%">
      <Formik
        initialValues={payorEnrollmentInitialValues}
        onSubmit={handleSubmit}
        validationSchema={payorEnrollmentValidation}
        validateOnMount
      >
        {formik => (
          <Box display="flex" flexDirection="column" gap="16px">
            {formik.isSubmitting ? (
              <Loader py={10} color="secondary" />
            ) : (
              <>
                <Box px={{ xs: 1, sm: 0 }}>
                  <Typography variant="h4" color="textPrimary">
                    Payer Enrollment
                  </Typography>
                  <Box mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Please ensure your information is correct to avoid delays processing payer enrollment applications.
                    </Typography>
                  </Box>
                </Box>
                <PayorEnrollmentFormContent />
                {submitError && (
                  <Typography variant="body2" color="error">{submitError}</Typography>
                )}
                <StickyButton>
                  <Box display="flex" gap="16px">
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleSkip}
                    >
                      Skip for now
                    </Button>
                    <SubmitButton disabledOnInvalid color="secondary">
                      Submit Enrollment
                    </SubmitButton>
                  </Box>
                </StickyButton>
              </>
            )}
          </Box>
        )}
      </Formik>
    </Box>
  );
};
